import React, { useState, useEffect } from "react";
import Container from "Components/Container/Container";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import Spinner from "Components/RequestHandler/Spinner";
import useFetch from "../../Hooks/useFetch";
import { useTranslation } from "react-i18next";
import CourseDisplay from "./CourseDisplay/CourseDisplay";

const Courses = () => {
  const { t } = useTranslation();
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [category, setCategory] = useState(null);
  const [courses, setCourses] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    pageSize: 10,
    hasNextPage: false,
    hasPrevPage: false,
  });
  const navigate = useNavigate();
  const { fetchData } = useFetch();

  // Get current page from URL or default to 1
  const currentPage = parseInt(searchParams.get("page")) || 1;

  // Function to fetch courses with pagination
  const fetchCourses = async (page = 1, pageSize = 10) => {
    try {
      setLoading(true);
      let endpoint = slug
        ? `courses/by-category/${slug}?page=${page}&pageSize=${pageSize}`
        : `courses?page=${page}&pageSize=${pageSize}`;

      const response = await fetchData(endpoint);

      if (response && response.success) {
        if (slug) {
          const { category, courses } = response.data;
          setCategory(category);
          setCourses(courses);
        } else {
          setCourses(response.data);
        }

        // Set pagination data
        if (response.pagination) {
          setPagination(response.pagination);
        }
      } else {
        setError(t("courses.failedToLoadData"));
      }

      setLoading(false);
    } catch (err) {
      console.error("Error fetching courses data:", err);
      setError(t("courses.failedToLoad"));
      setLoading(false);
    }
  };

  // Handle page change
  const handlePageChange = (page) => {
    setSearchParams({ page: page.toString() });
  };

  // Initial data load based on URL parameters
  useEffect(() => {
    fetchCourses(currentPage);
  }, [slug, currentPage]);

  if (error) {
    return (
      <main className="py-10">
        <Container>
          <div className="text-center text-red-500">{error}</div>
        </Container>
      </main>
    );
  }

  return (
    <main className="py-10">
      <Container>
        {loading ? (
          <div className="flex min-h-[100vh] justify-center">
            <Spinner />
          </div>
        ) : (
          <div className="  ">
            <div className="mb-10 text-center">
              <h1 className="text-2xl lg:text-3xl font-bold mb-2 font-title">
                {category?.title || t("courses.allCourses")}
              </h1>
              <p className="text-gray-500">
                {courses.length} {t("courses.coursesAvailable")}
              </p>
            </div>
            <CourseDisplay
              courses={courses}
              loading={loading}
              error={error}
              pagination={pagination}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </Container>
    </main>
  );
};

export default Courses;
